import React, { useState, useEffect } from "react";
import SEO from "../components/SEO";
import { API_BASE_URL } from "../config/api";

const translations = {
  tr: {
    title: "DUYURULAR",
    description: "Fransa hukukundaki güncel gelişmeler, içtihatlar ve uygulamalara dair uzman değerlendirmelerimiz.",
    readMore: "Devamını Oku",
    loading: "Yükleniyor...",
    noArticles: "Henüz makale bulunmamaktadır.",
    error: "Makaleler yüklenirken bir hata oluştu.",
    retry: "Tekrar Dene"
  },
  en: {
    title: "Announcements",
    description: "Our expert insights on recent legal developments, case law, and administrative practices in France.",
    readMore: "Read More",
    loading: "Loading...",
    noArticles: "No articles found.",
    error: "Error loading articles.",
    retry: "Try Again"
  },
  fr: {
    title: "Annonces",
    description: "Nos analyses d’experts sur les évolutions juridiques, la jurisprudence et les pratiques administratives en France.",
    readMore: "Lire la Suite",
    loading: "Chargement...",
    noArticles: "Aucun article trouvé.",
    error: "Erreur lors du chargement des articles.",
    retry: "Réessayer"
  }
};

const responsiveStyles = `
  .articles-grid.row {
    display: flex !important;
    flex-wrap: wrap !important;
    justify-content: center !important;
  }

  @media (min-width: 992px) {
    .articles-grid .article-col {
      display: flex !important;
    }
    .articles-grid .article-col .article-card {
      flex: 1;
    }
  }

  @media (max-width: 991px) {
    .articles-grid.row {
      flex-direction: column !important;
      align-items: center !important;
    }
    .articles-grid .article-col {
      width: 90% !important;
      max-width: 500px !important;
      float: none !important;
      padding-left: 0 !important;
      padding-right: 0 !important;
    }
  }
`;

export const ArticlesListPage = ({ lang, isDarkMode, onArticleClick }) => {
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const currentLang = lang ? lang.toUpperCase() : 'TR';
  const t = translations[lang?.toLowerCase()] || translations.tr;

  useEffect(() => {
    const controller = new AbortController();

    const fetchArticles = async () => {
      setLoading(true);
      setError('');

      try {
        const response = await fetch(`${API_BASE_URL}/api/article`, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json',
          },
          signal: controller.signal
        });

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = await response.json();
        console.log('Articles loaded:', data.length);
        setArticles(data);
      } catch (err) {
        // AbortError'u sessizce geç (component unmount olduğunda normal)
        if (err.name === 'AbortError') return;
        console.error('Fetch articles error:', err);
        setError(t.error);
      } finally {
        if (!controller.signal.aborted) {
          setLoading(false);
        }
      }
    };

    fetchArticles();

    return () => controller.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleRetry = () => {
    setLoading(true);
    setError('');

    fetch(`${API_BASE_URL}/api/article`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
      }
    })
      .then(res => {
        if (!res.ok) throw new Error(`HTTP error! status: ${res.status}`);
        return res.json();
      })
      .then(data => {
        setArticles(data);
        setLoading(false);
      })
      .catch(err => {
        console.error('Retry error:', err);
        setError(t.error);
        setLoading(false);
      });
  };

  const handleArticleClick = (articleId) => {
    if (onArticleClick) {
      onArticleClick(articleId);
    }
  };

  const getLocalizedField = (article, field) => {
    const langField = `${field}${currentLang}`;
    const fallbackField = `${field}TR`;
    return article[langField] || article[fallbackField] || '';
  };

  const seoContent = {
    tr: {
      title: "Makaleler",
      description: "Hukuk alanındaki güncel gelişmeler, içtihatlar ve uygulama örnekleri hakkında uzman görüşlerimizi paylaşıyoruz.",
      keywords: "hukuk makaleleri, içtihat, yargıtay kararları, hukuk yazıları, güncel hukuk gelişmeleri"
    },
    en: {
      title: "Articles",
      description: "We share our expert opinions on current developments, case law and practical examples in the field of law.",
      keywords: "legal articles, case law, supreme court decisions, legal writings, current legal developments"
    },
    fr: {
      title: "Articles",
      description: "Nous partageons nos opinions d'experts sur les développements actuels, la jurisprudence et les exemples pratiques dans le domaine du droit.",
      keywords: "articles juridiques, jurisprudence, décisions de la cour suprême, écrits juridiques, développements juridiques actuels"
    }
  };

  const seo = seoContent[lang?.toLowerCase()] || seoContent.tr;

  const cardStyle = {
    cursor: 'pointer',
    marginBottom: '30px',
    transition: 'transform 0.3s ease',
    background: isDarkMode ? '#2a2a2a' : '#fff',
    border: isDarkMode ? '1px solid rgba(255,255,255,0.1)' : '1px solid #e0e0e0',
    borderRadius: '12px',
    overflow: 'hidden',
    boxShadow: isDarkMode
      ? '0 4px 15px rgba(0,0,0,0.3)'
      : '0 4px 15px rgba(0,0,0,0.08)',
    textAlign: 'center'
  };

  return (
    <div className="articles-list-page" style={{ paddingTop: '80px', paddingBottom: '40px' }}>
      <style>{responsiveStyles}</style>

      <SEO
        title={seo.title}
        description={seo.description}
        keywords={seo.keywords}
        url="/articles"
        lang={currentLang.toLowerCase()}
        image="img/portfolio/01-small.jpg"
      />
      <div className="container">
        <div className="section-title" style={{ marginTop: '60px', marginBottom: '60px', textAlign: 'center' }}>
          <h2>{t.title}</h2>
          <p>{t.description}</p>
        </div>

        {/* Loading State */}
        {loading && (
          <div style={{ textAlign: 'center', padding: '60px 20px' }}>
            <i className="fa fa-spinner fa-spin" style={{ fontSize: '48px', color: '#1e7a46' }}></i>
            <h3 style={{ marginTop: '20px', color: '#666' }}>{t.loading}</h3>
          </div>
        )}

        {/* Error State */}
        {error && !loading && (
          <div style={{ textAlign: 'center', padding: '60px 20px' }}>
            <i className="fa fa-exclamation-circle" style={{ fontSize: '48px', color: '#dc3545' }}></i>
            <h3 style={{ marginTop: '20px', color: '#666' }}>{error}</h3>
            <button
              onClick={handleRetry}
              style={{
                marginTop: '20px',
                padding: '12px 30px',
                background: '#1e7a46',
                color: '#fff',
                border: 'none',
                borderRadius: '25px',
                cursor: 'pointer',
                fontSize: '16px'
              }}
            >
              <i className="fa fa-refresh"></i> {t.retry}
            </button>
          </div>
        )}

        {/* No Articles State */}
        {!loading && !error && articles.length === 0 && (
          <div style={{ textAlign: 'center', padding: '60px 20px' }}>
            <i className="fa fa-file-text-o" style={{ fontSize: '48px', color: '#999' }}></i>
            <h3 style={{ marginTop: '20px', color: '#666' }}>{t.noArticles}</h3>
          </div>
        )}

        {/* Makale Kartları */}
        {!loading && !error && articles.length > 0 && (
          <div className="row articles-grid">
            {articles.map((article) => {
              const title = getLocalizedField(article, 'title');
              const subtitle = getLocalizedField(article, 'subtitle');
              const description = getLocalizedField(article, 'description');

              return (
                <div key={article.id} className="col-sm-6 col-md-4 col-lg-4 article-col" style={{ marginBottom: '30px' }}>
                  <div
                    className="article-card"
                    onClick={() => handleArticleClick(article.id)}
                    style={cardStyle}
                  >
                    {article.image && (
                      <div className="article-image">
                        <img src={article.image} alt={title} style={{ width: '100%', height: 'auto' }} />
                        {article.category && (
                          <div className="article-category">{article.category}</div>
                        )}
                      </div>
                    )}
                    <div className="article-content" style={{ padding: '20px' }}>
                      <div className="article-meta-info" style={{ marginBottom: '10px', fontSize: '14px', color: '#999', justifyContent: 'center' }}>
                        <span className="article-date">{article.date}</span>
                        {article.readTime && (
                          <span className="article-read-time" style={{ marginLeft: '15px' }}>
                            <i className="fa fa-clock-o"></i> {article.readTime}
                          </span>
                        )}
                      </div>
                      <h3 className="article-title" style={{ fontSize: '22px', marginBottom: '10px' }}>{title}</h3>
                      {subtitle && (
                        <h4 className="article-subtitle" style={{ fontSize: '16px', color: isDarkMode ? '#bbb' : '#666', marginBottom: '10px' }}>{subtitle}</h4>
                      )}
                      <p className="article-description" style={{ color: isDarkMode ? '#bbb' : '#666', marginBottom: '15px' }}>{description}</p>
                      <span className="read-more-btn" style={{ color: '#1e7a46', fontWeight: '600' }}>
                        {t.readMore} <i className="fa fa-arrow-right"></i>
                      </span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default ArticlesListPage;